import { useTeamStore } from '../../teamStore';
import { useUserStore } from '../../userStore';
import type { Conversation, WorkspaceMode } from '@auw/workspace-utils';

export const GROUP_AGENT_MENTION_RE = /(^|[\s([{"'])@askyour\.ai(?=$|[\s)\]}",.!?:;'"])/i;

export interface BillingContext {
  scope: 'personal' | 'team';
  teamId: string | null;
  remainingTokens: number | null;
}

export interface SendMessageOptions {
  forceInvokeAgent?: boolean;
  modeOverride?: WorkspaceMode;
}

interface ResolveSendMessageTargetArgs {
  activeId: string | null;
  activeMode: WorkspaceMode;
  conversations: Record<string, Conversation>;
  createConversation: (opts?: {
    mode?: WorkspaceMode;
  }) => string;
  content: string;
  options?: SendMessageOptions;
}

export interface SendMessageTargetResolution {
  requestedMode: WorkspaceMode;
  conversationId: string;
  previewConversation: Conversation | null;
  shouldInvokeAgentPreview: boolean;
  billingContext: BillingContext;
}

export function resolveBillingContext(): BillingContext {
  const { workspaceView, teams } = useTeamStore.getState();
  const personalUsage = useUserStore.getState().usage;

  if (workspaceView === 'personal' || !workspaceView) {
    return {
      scope: 'personal',
      teamId: null,
      remainingTokens: Math.max(0, personalUsage.tokensLimit - personalUsage.tokensUsed),
    };
  }

  const selectedTeam = teams.find((team) => team.team_id === workspaceView);
  if (!selectedTeam) {
    return {
      scope: 'team',
      teamId: workspaceView,
      remainingTokens: null,
    };
  }

  const myAllocated =
    typeof selectedTeam.my_tokens_allocated === 'number'
      ? selectedTeam.my_tokens_allocated
      : null;
  const myUsed =
    typeof selectedTeam.my_tokens_used === 'number'
      ? selectedTeam.my_tokens_used
      : null;
  const hasMemberQuota = myAllocated !== null && myUsed !== null;

  return {
    scope: 'team',
    teamId: workspaceView,
    remainingTokens: hasMemberQuota
      ? Math.max(0, myAllocated - myUsed)
      : Math.max(0, selectedTeam.tokens_total - selectedTeam.tokens_used),
  };
}

export function resolveRequestedWorkspaceMode(
  activeMode: WorkspaceMode,
  modeOverride?: WorkspaceMode,
): WorkspaceMode {
  const fallbackMode =
    activeMode === 'widget' || activeMode === 'feed' || activeMode === 'tools'
      ? 'agent'
      : activeMode;
  return modeOverride ?? fallbackMode ?? 'agent';
}

export function resolveSendMessageTarget({
  activeId,
  activeMode,
  conversations,
  createConversation,
  content,
  options,
}: ResolveSendMessageTargetArgs): SendMessageTargetResolution {
  const requestedMode = resolveRequestedWorkspaceMode(activeMode, options?.modeOverride);
  let conversationId = activeId;

  const activeConversationCandidate = conversationId
    ? (conversations[conversationId] ?? null)
    : null;
  const previewConversation = (
    activeConversationCandidate
    && activeConversationCandidate.mode === requestedMode
    && activeConversationCandidate.workspaceKind === 'page'
  )
    ? activeConversationCandidate
    : null;

  if (!previewConversation) {
    conversationId = null;
  }

  const shouldInvokeAgentPreview =
    !previewConversation?.sharedGroup
    || !!options?.forceInvokeAgent
    || GROUP_AGENT_MENTION_RE.test(content);

  if (!conversationId || !conversations[conversationId]) {
    conversationId = createConversation({ mode: requestedMode });
  }

  return {
    requestedMode,
    conversationId,
    previewConversation,
    shouldInvokeAgentPreview,
    billingContext: resolveBillingContext(),
  };
}
